import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import BASE_URL from '../config';

const SchedulePost = () => {
  const [formData, setFormData] = useState({
    title: '',
    scheduled_publish_time: '',
    image_url: ''
  });
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Configure Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        blockquote: false
      }),
      Bold,
      Italic,
      BulletList,
      OrderedList
    ],
    content: formData.title,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        title: editor.getHTML()
      }));
    },
  });

  // Convert Facebook post message back to HTML for editing
  const convertFacebookToHtml = (text) => {
    if (!text) return '';
    
    // Convert Facebook's *bold* and _italics_ back to HTML
    let html = text
      .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')  // *bold* to <strong>
      .replace(/_([^_]+)_/g, '<em>$1</em>');          // _italics_ to <em>
    
    // Convert lists (assuming Facebook keeps the • and 1. format)
    html = html
      .replace(/•\s([^\n]+)/g, '<li>$1</li>')        // Bullet points
      .replace(/(\d+)\.\s([^\n]+)/g, '<li>$2</li>'); // Numbered lists
    
    // Wrap list items in proper tags
    if (html.includes('<li>')) {
      html = html.replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');
    }
    
    // Convert line breaks to <br> tags
    html = html.replace(/\n/g, '<br>');
    
    return html;
  };

  // Convert HTML to Facebook-compatible text
  const convertForFacebook = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Convert bold to asterisks
    tempDiv.querySelectorAll('strong, b').forEach(el => {
      el.replaceWith(`*${el.textContent}*`);
    });
    
    // Convert italics to underscores
    tempDiv.querySelectorAll('em, i').forEach(el => {
      el.replaceWith(`_${el.textContent}_`);
    });
    
    // Convert lists
    tempDiv.querySelectorAll('li').forEach(el => {
      const isOrdered = el.parentElement.tagName === 'OL';
      const prefix = isOrdered 
        ? `${Array.from(el.parentElement.children).indexOf(el) + 1}.` 
        : '•';
      el.replaceWith(`${prefix} ${el.textContent}\n`);
    });
    
    // Handle line breaks
    tempDiv.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
    tempDiv.querySelectorAll('p').forEach(p => {
      p.insertAdjacentText('afterend', '\n');
    });
    
    // Clean up and return
    return tempDiv.textContent
      .replace(/\n\s*\n/g, '\n\n')  // Preserve intentional line breaks
      .trim();
  };

  // Format datetime for Facebook API
  const formatForBackend = (datetimeLocal) => {
    const date = new Date(datetimeLocal);
    return date.toISOString().replace('Z', '+0000');
  };

  // Fetch Facebook access token
  const fetchAccessToken = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}api/proxy/get-facebook-token`);
      setAccessToken(response.data.access_token);
    } catch (err) {
      setError('Failed to get access token');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load scheduled posts
  const loadScheduledPosts = async () => {
    if (!accessToken) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}api/proxy/get-scheduled-posts`, {
        params: { access_token: accessToken }
      });
      setScheduledPosts(response.data.data || []);
    } catch (err) {
      setError('Failed to load scheduled posts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const facebookText = convertForFacebook(formData.title);

      const postData = {
        message: facebookText,
        scheduled_publish_time: formatForBackend(formData.scheduled_publish_time),
        access_token: accessToken,
        ...(formData.image_url && { image_url: formData.image_url }),
      };

      const endpoint = editingPost 
        ? `${BASE_URL}api/proxy/update-scheduled-post`
        : `${BASE_URL}api/proxy/schedule-facebook-post`;

      await axios.post(endpoint, 
        editingPost ? { ...postData, post_id: editingPost.id } : postData
      );

      setSuccess(editingPost ? 'Post updated successfully!' : 'Post scheduled successfully!');
      resetForm();
      loadScheduledPosts();
    } catch (err) {
      setError(err.response?.data?.error || 
        (editingPost ? 'Failed to update post' : 'Failed to schedule post'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ title: '', scheduled_publish_time: '', image_url: '' });
    setImagePreview(null);
    setEditingPost(null);
    if (editor) editor.commands.clearContent();
  };

  // Edit existing post
  const handleEdit = (post) => {
    const fbDate = new Date(post.scheduled_publish_time * 1000);
    const localDate = new Date(fbDate.getTime() - (fbDate.getTimezoneOffset() * 60000))
                      .toISOString()
                      .slice(0, 16);
    
    // Convert Facebook post back to HTML for editing
    const htmlContent = convertFacebookToHtml(post.message);
    
    setEditingPost(post);
    setFormData({
      title: htmlContent,
      scheduled_publish_time: localDate,
      image_url: post.attachments?.data?.[0]?.media?.image?.src || ''
    });
    setImagePreview(post.attachments?.data?.[0]?.media?.image?.src || null);
  };

  // Delete post
  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      setLoading(true);
      await axios.get(`${BASE_URL}api/proxy/delete-scheduled-post`, {
        params: { 
          post_id: postId,
          access_token: accessToken
        }
      });

      setSuccess('Post deleted successfully!');
      loadScheduledPosts();
    } catch (err) {
      setError('Failed to delete post');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle image URL insertion
  const handleImageInsert = () => {
    const url = prompt('Enter image URL');
    if (url) {
      setFormData(prev => ({
        ...prev,
        image_url: url
      }));
      setImagePreview(url);
    }
  };

  // Remove image preview
  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image_url: ''
    }));
    setImagePreview(null);
  };

  // Toolbar component
  const MenuBar = ({ editor }) => {
    if (!editor) return null;

    return (
      <div className="editor-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold (will show as *text* on Facebook)"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic (will show as _text_ on Facebook)"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Numbered List"
        >
          1. List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHardBreak().run()}
          title="Line Break"
        >
          ↵
        </button>
      </div>
    );
  };

  // Initialize
  useEffect(() => {
    fetchAccessToken();
  }, []);

  useEffect(() => {
    if (accessToken) {
      loadScheduledPosts();
    }
  }, [accessToken]);

  // Update editor when editing post
  useEffect(() => {
    if (editor && editingPost) {
      editor.commands.setContent(formData.title);
    }
  }, [editingPost, editor, formData.title]);

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Facebook Post Scheduler</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <h4 className="card-title">
            {editingPost ? 'Edit Scheduled Post' : 'Schedule New Post'}
          </h4>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Post Content</label>
              
              <div className="border rounded p-2">
                <MenuBar editor={editor} />
                <EditorContent 
                  editor={editor} 
                  className="editor-content"
                  style={{ minHeight: '200px', padding: '10px' }}
                />
              </div>
            </div>
            
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label">Image</label>
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleImageInsert}
                >
                  {imagePreview ? 'Change Image' : 'Add Image'}
                </button>
              </div>
              
              {imagePreview && (
                <div className="image-preview-container border rounded p-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Image Preview</small>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleRemoveImage}
                    >
                      Remove
                    </button>
                  </div>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="img-thumbnail"
                    style={{ maxWidth: '200px', maxHeight: '200px' }}
                  />
                </div>
              )}
            </div>
            
            <div className="mb-3">
              <label className="form-label">Scheduled Time</label>
              <input
                type="datetime-local"
                name="scheduled_publish_time"
                className="form-control"
                value={formData.scheduled_publish_time}
                onChange={(e) => setFormData({
                  ...formData, 
                  scheduled_publish_time: e.target.value
                })}
                required
              />
            </div>
            
            <div className="d-flex gap-2">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  editingPost ? 'Update Post' : 'Schedule Post'
                )}
              </button>
              
              {editingPost && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h4 className="card-title">Scheduled Posts</h4>
          
          {loading && scheduledPosts.length === 0 ? (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : scheduledPosts.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Post ID</th>
                    <th>Message Preview</th>
                    <th>Image</th>
                    <th>Scheduled Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledPosts.map((post, index) => (
                    <tr key={post.id}>
                      <td>{index + 1}</td>
                      <td>{post.id}</td>
                      <td>
                        {post.message ? (
                          <div className="text-truncate" style={{ maxWidth: '200px' }}>
                            {post.message.replace(/<\/?[^>]+(>|$)/g, "")}
                          </div>
                        ) : 'No message'}
                      </td>
                      <td>
                        {post.attachments?.data?.[0]?.media?.image?.src ? (
                          <img 
                            src={post.attachments.data[0].media.image.src} 
                            alt="Post" 
                            style={{ maxWidth: '50px', maxHeight: '50px' }}
                          />
                        ) : 'No image'}
                      </td>
                      <td>
                        {post.scheduled_publish_time ? 
                          new Date(post.scheduled_publish_time * 1000).toLocaleString() : 
                          'N/A'}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEdit(post)}
                            disabled={loading}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(post.id)}
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">
              No scheduled posts found
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .editor-toolbar {
          display: flex;
          gap: 8px;
          padding: 8px;
          border-bottom: 1px solid #ddd;
          background: #f8f9fa;
          flex-wrap: wrap;
        }
        .editor-toolbar button {
          background: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 4px 8px;
          cursor: pointer;
          min-width: 32px;
        }
        .editor-toolbar button.is-active {
          background: #e9ecef;
        }
        .editor-content {
          min-height: 200px;
          padding: 10px;
        }
        .editor-content:focus {
          outline: none;
        }
        .image-preview-container {
          background-color: #f8f9fa;
          padding: 10px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default SchedulePost;